/**
 * NOTIFICATION SETTINGS COMPONENT
 * 
 * Manages user email notification preferences:
 * - Enable/disable all email notifications
 * - PublicWishlist alerts (when wishlist items appear on destash)
 * - Destash inquiries (when someone inquires about your items)
 * - Favorite seller posts (when a seller you follow posts new destash items)
 */

import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Loader2, Check } from 'lucide-react';

interface NotificationSettingsData {
  emailNotifications: boolean;
  wishlistAlerts: boolean;
  destashInquiries: boolean;
  favoriteSellerPosts: boolean;  // NEW
  email: string;
}

const defaultSettings: NotificationSettingsData = {
  emailNotifications: false,
  wishlistAlerts: false,
  destashInquiries: false,
  favoriteSellerPosts: false,
  email: ''
};

export default function NotificationSettings() {
  const [settings, setSettings] = useState<NotificationSettingsData>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Get user's account email
  const userEmail = auth.currentUser?.email || '';

  // Load current settings
  useEffect(() => {
    const loadSettings = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setSettings({
            emailNotifications: data.notificationSettings?.emailNotifications ?? false,
            wishlistAlerts: data.notificationSettings?.wishlistAlerts ?? true,
            destashInquiries: data.notificationSettings?.destashInquiries ?? true,
            favoriteSellerPosts: data.notificationSettings?.favoriteSellerPosts ?? true,  // NEW
            email: user.email || '' // Always use account email
          });
        } else {
          setSettings({
            ...defaultSettings,
            email: user.email || ''
          });
        }
      } catch (err) {
        console.error('Error loading notification settings:', err);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Save settings
  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setSaving(true);
    setError('');

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        notificationSettings: {
          emailNotifications: settings.emailNotifications,
          wishlistAlerts: settings.wishlistAlerts,
          destashInquiries: settings.destashInquiries,
          favoriteSellerPosts: settings.favoriteSellerPosts,  // NEW
          email: user.email || '' // Always use account email
        }
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Error saving notification settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-6 mb-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-glass" />
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-6 mb-6">
      {/* Header */}
      <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
        📧 Notifications
      </h2>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Master toggle */}
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <span className="font-medium text-glass">Enable Email Notifications</span>
            <p className="text-sm text-glass-muted">
              Notifications will be sent to {userEmail}
            </p>
          </div>
          <input
            type="checkbox"
            checked={settings.emailNotifications}
            onChange={(e) => setSettings(prev => ({ ...prev, emailNotifications: e.target.checked }))}
            className="w-5 h-5 text-glass rounded focus:ring-slate-500"
          />
        </label>

        {/* Individual notification types - always visible */}
        <div className={`pl-4 border-l-2 border-slate-600 space-y-3 ${!settings.emailNotifications ? 'opacity-50' : ''}`}>
          {/* PublicWishlist alerts */}
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-glass">⭐ Wishlist Alerts</span>
              <p className="text-sm text-glass-muted">
                When items on your wishlist appear for destash
              </p>
            </div>
            <input
              type="checkbox"
              checked={settings.wishlistAlerts}
              onChange={(e) => setSettings(prev => ({ ...prev, wishlistAlerts: e.target.checked }))}
              disabled={!settings.emailNotifications}
              className="w-5 h-5 text-pink-500 rounded focus:ring-pink-500 disabled:opacity-50"
            />
          </label>

          {/* Destash inquiries */}
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-glass">🏷️ Destash Inquiries</span>
              <p className="text-sm text-glass-muted">
                When someone inquires about your destash items
              </p>
            </div>
            <input
              type="checkbox"
              checked={settings.destashInquiries}
              onChange={(e) => setSettings(prev => ({ ...prev, destashInquiries: e.target.checked }))}
              disabled={!settings.emailNotifications}
              className="w-5 h-5 text-green-500 rounded focus:ring-green-500 disabled:opacity-50"
            />
          </label>

          {/* NEW: Favorite seller posts */}
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-glass">❤️ Favorite Seller Posts</span>
              <p className="text-sm text-glass-muted">
                When a seller you follow posts new destash items
              </p>
            </div>
            <input
              type="checkbox"
              checked={settings.favoriteSellerPosts}
              onChange={(e) => setSettings(prev => ({ ...prev, favoriteSellerPosts: e.target.checked }))}
              disabled={!settings.emailNotifications}
              className="w-5 h-5 text-red-500 rounded focus:ring-red-500 disabled:opacity-50"
            />
          </label>
        </div>

        {/* Save button */}
        <div className="pt-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-default ${
              saved 
                ? 'bg-green-500 text-white' 
                : 'btn-primary'
            } disabled:opacity-50`}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="icon-sm" />
                Saved!
              </>
            ) : (
              '💾 Save Notification Settings'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
